import { useState } from "react";
import { toast } from "sonner";
import api, { errorText } from "../../../lib/api";
import { usd } from "../../../lib/dash";
import { Button } from "../../ui/button";

const MODES = [["keep", "Keep the allowance"], ["set", "Set it to"], ["default", "Back to the default"]];   // no unlimited, ever

export const allowanceBody = ({ mode, amount, reset, reason }) => ({
  ...(mode === "set" ? { allowance_usd: Number(amount) } : {}),
  clear_allowance: mode === "default", reset, reason: reason.trim(),
});

const cap = (x) => `${usd(x.cap_usd)}${x.window === "month" ? " a month" : ""}`;
const input = "h-8 rounded-lg border border-input bg-background px-2 text-[12.5px]";

// Give back allowance through user_spend.adjust: a reason is required and the change goes to the admin audit.
export const AllowanceForm = ({ id, spend, onDone }) => {
  const [f, setF] = useState({ mode: "keep", amount: "", reset: true, reason: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const amountOk = f.mode !== "set" || (f.amount !== "" && Number(f.amount) >= 0 && Number(f.amount) <= 1000);
  const ready = f.reason.trim().length >= 3 && amountOk && (f.mode !== "keep" || f.reset);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/accounts/${id}/allowance`, allowanceBody(f));
      setResult(data);
      setF({ ...f, reason: "" });
      toast.success("Allowance saved · written to the admin audit");
      onDone();
    } catch (err) {
      setError(errorText(err, "Could not change the allowance."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form data-testid="allowance-form" onSubmit={save} className="space-y-2.5 text-[12.5px]">
      <p className="text-muted-foreground">
        Now {usd(spend.used_usd)} used{spend.window === "month" ? " this month" : ""}, limit {cap(spend)} ($0-1000, never unlimited).
        Nothing recorded is erased: a reset moves the used amount to “given back”.
      </p>
      <label className="flex items-center gap-2 font-medium">
        <input type="checkbox" data-testid="allowance-reset" checked={f.reset} onChange={(e) => setF({ ...f, reset: e.target.checked })} />
        Reset what they used to $0.00
      </label>
      <div className="flex flex-wrap items-center gap-1.5">
        {MODES.map(([k, label]) => (
          <button key={k} type="button" data-testid={`allowance-mode-${k}`} aria-pressed={f.mode === k} onClick={() => setF({ ...f, mode: k })}
            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${f.mode === k ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
        {f.mode === "set" && (
          <input type="number" data-testid="allowance-amount" aria-label="Allowance in dollars" min="0" max="1000" step="0.5" placeholder="$"
            value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={`${input} w-24`} />
        )}
      </div>
      <input data-testid="allowance-reason" value={f.reason} maxLength={200} placeholder="Reason (required, goes to the admin audit)"
        onChange={(e) => setF({ ...f, reason: e.target.value })} className={`${input} w-full`} />
      {error && <p data-testid="allowance-error" className="text-rose-600">{error}</p>}
      {result && (
        <p data-testid="allowance-result" className="rounded-lg bg-emerald-50 px-2.5 py-1.5 text-emerald-800">
          Used {usd(result.before.used_usd)} → {usd(result.after.used_usd)} · limit {cap(result.before)} → {cap(result.after)}
        </p>
      )}
      <Button type="submit" size="sm" data-testid="allowance-submit" disabled={!ready || busy} className="rounded-full">Save allowance</Button>
    </form>
  );
};
